import React, { memo, useMemo } from 'react';

import { StreamMessageEvents } from '@/entities/ai';
import {
  StreamEventGroup,
  groupSseEventsByHierarchy,
  isSupportedSseTaskEvent,
  getSseEventDisplayName,
  SupportedSseTaskEvents,
} from './AgentTask.utils';
import { FINAL_STATUS_EVENT } from './AgentTask.constants';
import { AgentTaskIcon } from './AgentTaskIcon';
import { TaskItem } from './TaskItem';

const getUniqueKey = (event: SupportedSseTaskEvents): string | undefined => {
  if (
    event.type === 'data-toolStart' ||
    event.type === 'data-toolComplete' ||
    event.type === 'data-toolError'
  ) {
    return event.data.callId;
  }
  if (event.type === 'data-agentStart' || event.type === 'data-agentComplete') {
    return event.data.agentId;
  }
  return undefined;
};

const StreamTaskItem = ({
  event,
  level = 0,
  children,
  isLastInGroup = false,
  isFirstInGroup = false,
  settled = false,
}: React.PropsWithChildren<{
  event: StreamMessageEvents;
  level?: number;
  isLastInGroup?: boolean;
  isFirstInGroup?: boolean;
  settled?: boolean;
}>) => {
  // Update if we have other events that need to be supported
  if (!isSupportedSseTaskEvent(event)) {
    return null;
  }
  return (
    <TaskItem
      event={event}
      level={level}
      isLastInGroup={isLastInGroup}
      isFirstInGroup={isFirstInGroup}
      getUniqueKey={getUniqueKey}
      getDisplayName={getSseEventDisplayName}
      renderIcon={(event) => <AgentTaskIcon data={event} settled={settled} />}
      testIdPrefix="sse-agent-task"
    >
      {children}
    </TaskItem>
  );
};

interface AgentTaskProps {
  events: StreamMessageEvents[];
  /** Closes the tree with the row that waits on the answer, which a finished reply leaves out. */
  live?: boolean;
  /** The reply has stopped streaming, so a call that never finished is drawn stopped, not running. */
  settled?: boolean;
}

export const AgentTask = memo(({ events, live = true, settled = false }: AgentTaskProps) => {
  const allEvents = useMemo(() => {
    const steps = events.filter(isSupportedSseTaskEvent);
    return live ? [...steps, FINAL_STATUS_EVENT] : steps;
  }, [events, live]);
  const eventGroups = useMemo(() => groupSseEventsByHierarchy(allEvents), [allEvents]);
  const renderEventGroup = (
    group: StreamEventGroup,
    index: number,
    isLastInGroup: boolean = false,
    isFirstInGroup: boolean = false,
  ) => {
    // Render child event groups recursively
    const childElements = group.children.map((childGroup, childIndex) =>
      renderEventGroup(
        childGroup,
        childIndex,
        childIndex === group.children.length - 1,
        childIndex === 0,
      ),
    );

    return (
      <StreamTaskItem
        key={`group-${index}`}
        event={group.root}
        level={group.level}
        isLastInGroup={isLastInGroup}
        isFirstInGroup={isFirstInGroup}
        settled={settled}
      >
        {childElements}
      </StreamTaskItem>
    );
  };

  return (
    <>
      {eventGroups.map((group, groupIndex) =>
        renderEventGroup(
          group,
          groupIndex,
          groupIndex === eventGroups.length - 1,
          groupIndex === 0,
        ),
      )}
    </>
  );
});

AgentTask.displayName = 'AgentTask';
